import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import MultiPhotoCapture from "@/components/MultiPhotoCapture";
import PhotoLightbox from "@/components/PhotoLightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { STATUS_LABEL, TYPE_LABEL } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, Sparkles, CheckCircle2, MessageSquare, Bot, Video, ExternalLink, ZoomIn, Users } from "lucide-react";

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function AssignmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [answers, setAnswers] = useState({});
  const [photoPaths, setPhotoPaths] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [err, setErr] = useState(false);

  const load = () => api.get(`/assignments/${id}`).then((r) => {
    setA(r.data);
    const sub = r.data.my_submission;
    if (sub?.answers) {
      const m = {}; sub.answers.forEach((x) => (m[x.no] = x.answer)); setAnswers(m);
    }
    const existing = (sub?.photo_paths && sub.photo_paths.length) ? sub.photo_paths : (sub?.photo_path ? [sub.photo_path] : []);
    if (existing.length) setPhotoPaths(existing);
  }).catch(() => setErr(true));
  useEffect(() => { setErr(false); load(); }, [id]);

  if (err) return (
    <div className="max-w-md mx-auto text-center py-20 space-y-4" data-testid="assignment-not-found">
      <p className="text-slate-500">Tugas tidak ditemukan atau sudah dihapus.</p>
      <Button onClick={() => navigate("/murid/tugas")} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="assignment-back-btn"><ArrowLeft className="w-4 h-4 mr-1.5" /> Kembali ke Tugas</Button>
    </div>
  );
  if (!a) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  const sub = a.my_submission;
  const st = STATUS_LABEL[sub?.status || "belum"];
  const locked = sub && !["belum", "revisi"].includes(sub.status);
  const isOnline = a.type === "online";
  const isInClass = a.type === "tatap_muka";
  const questionPhotos = (a.photo_paths && a.photo_paths.length) ? a.photo_paths : (a.photo_path ? [a.photo_path] : []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { assignment_id: id };
      if (isOnline) {
        payload.answers = (a.questions || []).map((q) => ({ no: q.no, answer: answers[q.no] || "" }));
      } else {
        if (!photoPaths.length) { toast.error("Unggah minimal 1 foto bukti kerjamu dulu ya 📸"); setSubmitting(false); return; }
        payload.photo_paths = photoPaths;
      }
      await api.post("/submissions", payload);
      toast.success("Tugas berhasil dikumpulkan! 🎉");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengumpulkan tugas");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button onClick={() => navigate("/murid/tugas")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-assignments">
        <ArrowLeft className="w-4 h-4" /> Kembali ke daftar tugas
      </button>

      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-medium">{TYPE_LABEL[a.type]}</span>
          <span className={`text-[11px] px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
          {a.ai_enabled && <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium"><Sparkles className="w-3 h-3" /> Dibantu AI</span>}
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-800 mt-3">{a.title}</h1>
        <p className="text-sm text-slate-500">{a.subject_name} · Kelas {a.kelas}</p>
        {a.instructions && <p className="mt-4 text-slate-700 bg-slate-50 rounded-xl p-4 leading-relaxed">{a.instructions}</p>}
        {questionPhotos.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-500 mb-2">Foto soal dari buku ({questionPhotos.length}) — ketuk untuk perbesar & putar:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {questionPhotos.map((p, i) => (
                <button key={i} type="button" onClick={() => setLightbox({ open: true, index: i })} className="group relative rounded-xl overflow-hidden border-2 border-teal-100" data-testid={`assignment-question-photo-${i}`}>
                  <img src={fileUrl(p)} alt={`Foto soal ${i + 1}`} className="w-full h-32 object-cover group-hover:scale-105 transition-transform" />
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-colors">
                    <span className="opacity-0 group-hover:opacity-100 bg-white/90 text-slate-800 text-xs font-semibold rounded-full px-3 py-1 flex items-center gap-1 transition-opacity"><ZoomIn className="w-3.5 h-3.5" /> Perbesar</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {a.video_url && (
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="assignment-video-card">
          <div className="flex items-center gap-2 text-slate-800 font-display font-semibold mb-3"><Video className="w-5 h-5 text-rose-600" /> Video Pembelajaran</div>
          {ytEmbed(a.video_url) ? (
            <div className="relative w-full rounded-xl overflow-hidden border-2 border-slate-100" style={{ paddingTop: "56.25%" }}>
              <iframe src={ytEmbed(a.video_url)} title="Video Pembelajaran" className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="assignment-video-iframe" />
            </div>
          ) : (
            <a href={a.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold transition-colors" data-testid="assignment-video-link">
              <ExternalLink className="w-5 h-5" /> Buka Video / Materi
            </a>
          )}
        </div>
      )}

      {/* final result */}
      {sub?.status === "dinilai" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6" data-testid="final-grade-card">
          <div className="flex items-center gap-2 text-emerald-800 font-display font-semibold"><CheckCircle2 className="w-5 h-5" /> Nilai Final Guru</div>
          <p className="font-display text-5xl font-bold text-emerald-700 mt-2">{sub.final_score}</p>
          {sub.teacher_feedback && (
            <div className="mt-3 flex items-start gap-2 text-sm text-emerald-800"><MessageSquare className="w-4 h-4 mt-0.5 shrink-0" /><p>{sub.teacher_feedback}</p></div>
          )}
        </div>
      )}

      {sub && ["dikumpulkan", "dianalisis", "menunggu_review"].includes(sub.status) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-800 flex items-center gap-2">
          <Bot className="w-5 h-5 shrink-0" /> Tugasmu sudah terkirim dan sedang diperiksa guru. Nilai akan muncul di sini.
        </div>
      )}

      {sub?.status === "revisi" && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 text-sm text-orange-800">
          <p className="font-semibold">Guru meminta kamu merevisi tugas ini.</p>
          {sub.teacher_feedback && <p className="mt-1">{sub.teacher_feedback}</p>}
        </div>
      )}

      {/* working area */}
      {isInClass ? (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-5 text-sm text-sky-800 flex items-start gap-2" data-testid="tatap-muka-info">
          <Users className="w-5 h-5 shrink-0 mt-0.5" />
          <p>Tugas ini dikerjakan <b>tatap muka di kelas</b>. Kamu tidak perlu mengunggah apa pun — nilaimu akan diisi langsung oleh guru dan muncul di sini. 😊</p>
        </div>
      ) : !locked && (
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-5">
          <h2 className="font-display font-semibold text-slate-800">{sub?.status === "revisi" ? "Perbaiki Jawabanmu" : "Kerjakan Tugas"}</h2>

          {isOnline ? (
            <div className="space-y-4">
              {(a.questions || []).map((q) => (
                <div key={q.no}>
                  <p className="text-sm font-medium text-slate-700">{q.no}. {q.text}</p>
                  <Input value={answers[q.no] || ""} onChange={(e) => setAnswers({ ...answers, [q.no]: e.target.value })} className="mt-1.5 rounded-xl" placeholder="Jawabanmu" data-testid={`answer-input-${q.no}`} />
                </div>
              ))}
              {(a.questions || []).length === 0 && <p className="text-sm text-slate-400">Tidak ada soal.</p>}
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-600 mb-3">{a.type === "catatan" ? "Tulis di buku lalu foto catatanmu (boleh beberapa foto)." : a.type === "pr" ? "Kerjakan PR di rumah lalu unggah foto hasilnya (boleh beberapa foto)." : "Kerjakan di buku lalu unggah foto hasilnya (boleh beberapa foto)."}</p>
              <MultiPhotoCapture category="submission" testIdPrefix="submission-photo" label="Foto Jawaban" paths={photoPaths} onChange={setPhotoPaths} />
            </div>
          )}

          <Button onClick={submit} disabled={submitting} className="w-full h-12 rounded-xl bg-teal-600 hover:bg-teal-700 font-semibold" data-testid="submit-assignment-btn">
            {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />} Kumpulkan Tugas
          </Button>
        </div>
      )}

      <PhotoLightbox
        photos={questionPhotos}
        index={lightbox.index}
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
    </div>
  );
}
